import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, HandHeart, Calendar, ShoppingBag, Users, BookOpen, Mic, ListChecks, Trash2, Eye, EyeOff, Home, HandCoins } from "lucide-react";
import { api, BRAND } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="min-h-[60vh] flex items-center justify-center text-white/90">Loading…</div>;
  if (!user.is_admin) return null;

  return (
    <div data-testid="page-dashboard" className="container-narrow pt-10 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <img src={BRAND.logoChurch} className="h-12 w-12 rounded-full ring-1 ring-[#D4AF37]/40" alt=""/>
          <div>
            <div className="label-luxe">Admin Dashboard</div>
            <h1 className="font-serif text-3xl">Welcome, {user.name?.split(" ")[0] || "Pastor"}.</h1>
          </div>
        </div>
        <button data-testid="dashboard-logout" onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost-gold !py-2 !px-4 text-sm">
          <LogOut className="w-4 h-4"/> Logout
        </button>
      </div>

      <Tabs defaultValue="prayers">
        <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="prayers" data-testid="tab-prayers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><HandHeart className="w-4 h-4 mr-2"/>Prayers</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><Mic className="w-4 h-4 mr-2"/>Bookings</TabsTrigger>
          <TabsTrigger value="veterans" data-testid="tab-veterans" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><Home className="w-4 h-4 mr-2"/>Veteran Housing</TabsTrigger>
          <TabsTrigger value="giving" data-testid="tab-giving" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><HandCoins className="w-4 h-4 mr-2"/>Giving</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="tab-enrollments" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><ListChecks className="w-4 h-4 mr-2"/>Blueprint</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><ShoppingBag className="w-4 h-4 mr-2"/>Orders</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><Users className="w-4 h-4 mr-2"/>Newsletter</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><ShoppingBag className="w-4 h-4 mr-2"/>Products</TabsTrigger>
          <TabsTrigger value="vlog" data-testid="tab-vlog" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><BookOpen className="w-4 h-4 mr-2"/>Vlog</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]"><Calendar className="w-4 h-4 mr-2"/>Schedule</TabsTrigger>
        </TabsList>

        <TabsContent value="prayers" className="mt-6"><PrayersPanel/></TabsContent>
        <TabsContent value="bookings" className="mt-6"><BookingsPanel/></TabsContent>
        <TabsContent value="veterans" className="mt-6"><VeteransPanel/></TabsContent>
        <TabsContent value="giving" className="mt-6"><GivingPanel/></TabsContent>
        <TabsContent value="enrollments" className="mt-6"><SimpleList endpoint="/admin/enrollments" emptyText="No enrollments yet." render={(e) => <li key={e.id} className="card-luxe">
          <div className="flex justify-between"><div className="font-serif text-lg">{e.name}</div><span className="text-xs px-2 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37]">{e.payment_method}</span></div>
          <div className="text-sm text-white/80 mt-1">{e.email} · {e.phone}</div>
          {e.notes && <p className="text-sm text-white/90 mt-2">{e.notes}</p>}
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mt-3">{new Date(e.created_at).toLocaleString()}</div>
        </li>}/></TabsContent>
        <TabsContent value="orders" className="mt-6"><OrdersPanel/></TabsContent>
        <TabsContent value="newsletter" className="mt-6"><SimpleList endpoint="/admin/newsletter" emptyText="No subscribers yet." render={(s) => <li key={s.id} className="card-luxe flex justify-between"><span>{s.email}</span><span className="text-xs text-white/80">{new Date(s.created_at).toLocaleDateString()}</span></li>}/></TabsContent>
        <TabsContent value="products" className="mt-6"><ProductsPanel/></TabsContent>
        <TabsContent value="vlog" className="mt-6"><VlogPanel/></TabsContent>
        <TabsContent value="schedule" className="mt-6"><SchedulePanel/></TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleList({ endpoint, render, emptyText }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then(r => setRows(r.data || [])).catch(()=>{}); }, [endpoint]);
  if (rows.length === 0) return <div className="card-luxe text-white/80 text-center py-12">{emptyText}</div>;
  return <ul className="space-y-3">{rows.map(render)}</ul>;
}

function PrayersPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/prayers").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const togglePublic = async (p) => {
    await api.patch(`/admin/prayers/${p.id}`, { is_public: !p.is_public });
    toast.success(p.is_public ? "Hidden from wall" : "Now public");
    load();
  };
  const del = async (id) => {
    if (!confirm("Delete this prayer?")) return;
    await api.delete(`/admin/prayers/${id}`);
    load();
  };
  if (rows.length === 0) return <div className="card-luxe text-white/80 text-center py-12">No prayer requests yet.</div>;
  return (
    <ul className="space-y-3">
      {rows.map(p => (
        <li key={p.id} className="card-luxe">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-serif text-lg">{p.name} <span className="text-xs text-white/80 font-sans">{p.email || ""}</span></div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full ${p.is_public ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-[#D91668]/15 text-[#F02078]"}`}>{p.is_public ? "Public" : "Private"}</span>
              <button onClick={() => togglePublic(p)} data-testid={`prayer-toggle-${p.id}`} className="text-xs text-white/90 hover:text-[#D4AF37] inline-flex items-center gap-1">
                {p.is_public ? <><EyeOff className="w-4 h-4"/>Hide</> : <><Eye className="w-4 h-4"/>Show</>}
              </button>
              <button onClick={() => del(p.id)} className="text-white/80 hover:text-[#F02078]"><Trash2 className="w-4 h-4"/></button>
            </div>
          </div>
          <p className="mt-3 text-white/90 italic leading-relaxed">"{p.request}"</p>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mt-3">{new Date(p.created_at).toLocaleString()}</div>
        </li>
      ))}
    </ul>
  );
}

function BookingsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/bookings").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success("Updated");
    load();
  };
  if (rows.length === 0) return <div className="card-luxe text-white/80 text-center py-12">No booking inquiries yet.</div>;
  return (
    <ul className="space-y-3">
      {rows.map(b => (
        <li key={b.id} className="card-luxe">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-serif text-lg">{b.name} <span className="text-xs text-white/80 font-sans">· {b.event_type}</span></div>
              <div className="text-xs text-white/80 mt-1">{b.email} · {b.phone} · {b.organization}</div>
            </div>
            <select value={b.status} onChange={(e)=>setStatus(b.id, e.target.value)} className="input-luxe !py-1 !px-3 text-xs w-auto">
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="confirmed">Confirmed</option>
              <option value="declined">Declined</option>
              <option value="done">Done</option>
            </select>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-3 text-xs text-white/80">
            <div><span className="text-[#D4AF37]">Date:</span> {b.event_date || "—"}</div>
            <div><span className="text-[#D4AF37]">Location:</span> {b.location || "—"}</div>
            <div><span className="text-[#D4AF37]">Budget:</span> {b.budget || "—"}</div>
          </div>
          <p className="mt-3 text-white/90 leading-relaxed text-sm">{b.message}</p>
        </li>
      ))}
    </ul>
  );
}

function VeteransPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/veteran-applications").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/veteran-applications/${id}`, { status });
    toast.success("Updated");
    load();
  };
  if (rows.length === 0) return <div className="card-luxe text-white/80 text-center py-12">No veteran housing applications yet.</div>;
  return (
    <ul className="space-y-3">
      {rows.map(a => (
        <li key={a.id} className="card-luxe" data-testid="veteran-app-row">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-serif text-lg">{a.name} <span className="text-xs text-white/80 font-sans">· {a.branch || "Branch not given"}</span></div>
              <div className="text-xs text-white/80 mt-1">{a.email} · {a.phone}</div>
            </div>
            <select value={a.status} onChange={(e)=>setStatus(a.id, e.target.value)} data-testid={`veteran-status-${a.id}`} className="input-luxe !py-1 !px-3 text-xs w-auto">
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="approved">Approved</option>
              <option value="waitlist">Waitlist</option>
              <option value="declined">Declined</option>
            </select>
          </div>
          <div className="text-xs text-white/80 mt-3"><span className="text-[#D4AF37]">Desired move-in:</span> {a.move_in_date || "—"}</div>
          {a.situation && <p className="mt-3 text-white/90 leading-relaxed text-sm">{a.situation}</p>}
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mt-3">{new Date(a.created_at).toLocaleString()}</div>
        </li>
      ))}
    </ul>
  );
}

function BetheaGoalEditor() {
  const [goal, setGoal] = useState("");
  useEffect(() => { api.get("/bethea/progress").then(r => setGoal(String(r.data.goal))); }, []);
  const save = async () => {
    try { await api.put("/admin/bethea/goal", { goal: Number(goal) }); toast.success("Goal updated"); }
    catch { toast.error("Enter a goal greater than 0"); }
  };
  return (
    <div className="card-luxe !p-4 flex flex-wrap items-center gap-3" data-testid="bethea-goal-editor">
      <span className="text-sm font-semibold">House of Bethea fundraising goal ($)</span>
      <input type="number" min="1" value={goal} onChange={e => setGoal(e.target.value)} className="input-luxe !py-2 w-40" data-testid="bethea-goal-input" />
      <button onClick={save} className="btn-gold !py-2 !px-4 text-sm" data-testid="bethea-goal-save">Save Goal</button>
    </div>
  );
}

function GivingPanel() {
  const [fund, setFund] = useState("church");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let live = true;
    api.get(`/admin/tithes?fund=${fund}`).then(r => { if (live) setRows(r.data || []); });
    return () => { live = false; };
  }, [fund]);
  const total = rows.reduce((s, t) => s + (t.amount || 0), 0);
  const toggleWall = async (t) => {
    await api.patch(`/admin/tithes/${t.id}`, { show_on_wall: !t.show_on_wall });
    setRows(rows.map(r => r.id === t.id ? { ...r, show_on_wall: !t.show_on_wall } : r));
    toast.success(t.show_on_wall ? "Hidden from Donor Wall" : "Shown on Donor Wall");
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {[["church", "Church Tithes & Offerings"], ["bethea", "House of Bethea Donations"]].map(([k, l]) => (
            <button key={k} onClick={() => setFund(k)} data-testid={`giving-fund-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border ${fund === k ? "bg-[#D4AF37] text-[#050814] border-[#D4AF37]" : "border-white/20 text-white/90"}`}>{l}</button>
          ))}
        </div>
        <div className="font-serif text-2xl text-[#D4AF37]" data-testid="giving-total">Total: ${total.toFixed(2)}</div>
      </div>
      {fund === "bethea" && <BetheaGoalEditor />}
      {rows.length === 0 ? <div className="card-luxe text-white/80 text-center py-12">No gifts yet.</div> : (
        <ul className="space-y-3">
          {rows.map(t => (
            <li key={t.id} className="card-luxe" data-testid="giving-row">
              <div className="flex justify-between">
                <div className="font-serif text-lg">{t.donor_name} <span className="text-xs text-white/80 font-sans">· {t.purpose}</span></div>
                <div className="font-serif text-xl text-[#D4AF37]">${t.amount?.toFixed(2)}</div>
              </div>
              <div className="text-xs text-white/80">{t.donor_email || "no email"} · {t.payment_method}</div>
              {fund === "bethea" && (
                <button onClick={() => toggleWall(t)} data-testid={`wall-toggle-${t.id}`}
                  className={`mt-2 text-xs px-3 py-1 rounded-full border ${t.show_on_wall ? "border-[#D4AF37] text-[#ECC23D]" : "border-white/20 text-white/80"}`}>
                  {t.show_on_wall ? "On Donor Wall · Hide" : "Not on Donor Wall · Show"}
                </button>
              )}
              {t.notes && <p className="text-sm text-white/90 mt-2">{t.notes}</p>}
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mt-3">{new Date(t.created_at).toLocaleString()}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrdersPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(r => setRows(r.data || [])); }, []);
  if (rows.length === 0) return <div className="card-luxe text-white/80 text-center py-12">No orders yet.</div>;
  return (
    <ul className="space-y-3">
      {rows.map(o => (
        <li key={o.id} className="card-luxe">
          <div className="flex justify-between">
            <div className="font-serif text-lg">{o.customer_name}</div>
            <div className="font-serif text-xl text-[#D4AF37]">${o.total?.toFixed(2)}</div>
          </div>
          <div className="text-xs text-white/80">{o.customer_email} · {o.payment_method}</div>
          <ul className="mt-3 text-sm text-white/90 list-disc pl-5">
            {(o.items || []).map((i, k) => <li key={k}>{i.title} × {i.qty} — ${(i.price * i.qty).toFixed(2)}</li>)}
          </ul>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mt-3">{new Date(o.created_at).toLocaleString()} · ref {o.id?.slice(0,8)}</div>
        </li>
      ))}
    </ul>
  );
}

function ProductsPanel() {
  const [rows, setRows] = useState([]);
  const blank = { title: "", description: "", price: 0, category: "apparel", image_url: "", badge: "", is_active: true };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/products").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/products", { ...f, price: Number(f.price) });
      setF(blank); load(); toast.success("Product added");
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/products/${id}`); load(); };
  return (
    <div className="space-y-6">
      <form onSubmit={create} className="card-luxe grid sm:grid-cols-2 gap-3">
        <input required placeholder="Title" value={f.title} onChange={(e)=>setF({...f, title:e.target.value})} className="input-luxe" data-testid="prod-title"/>
        <input required type="number" step="0.01" placeholder="Price" value={f.price} onChange={(e)=>setF({...f, price:e.target.value})} className="input-luxe" data-testid="prod-price"/>
        <select value={f.category} onChange={(e)=>setF({...f, category:e.target.value})} className="input-luxe">
          <option value="book">Book</option><option value="apparel">Apparel</option><option value="program">Program</option>
        </select>
        <input placeholder="Badge (e.g. Bestseller)" value={f.badge} onChange={(e)=>setF({...f, badge:e.target.value})} className="input-luxe"/>
        <input placeholder="Image URL" value={f.image_url} onChange={(e)=>setF({...f, image_url:e.target.value})} className="input-luxe sm:col-span-2"/>
        <textarea required placeholder="Description" value={f.description} onChange={(e)=>setF({...f, description:e.target.value})} className="input-luxe sm:col-span-2" rows={2}/>
        <button disabled={busy} className="btn-gold sm:col-span-2" data-testid="prod-create">{busy ? "Saving…" : "Add Product"}</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map(p => (
          <div key={p.id} className="card-luxe flex gap-3">
            <img src={p.image_url} alt="" className="w-20 h-20 rounded-lg object-cover"/>
            <div className="flex-1">
              <div className="font-serif text-lg">{p.title}</div>
              <div className="text-xs text-white/80">{p.category} · ${p.price?.toFixed?.(2)}</div>
              <p className="text-xs text-white/80 mt-1 line-clamp-2">{p.description}</p>
            </div>
            <button onClick={() => del(p.id)} className="text-white/80 hover:text-[#F02078] self-start"><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VlogPanel() {
  const [rows, setRows] = useState([]);
  const blank = { title: "", excerpt: "", body: "", youtube_url: "", thumbnail_url: "", is_published: true };
  const [f, setF] = useState(blank);
  const load = () => api.get("/admin/vlog").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    await api.post("/admin/vlog", f);
    setF(blank); load(); toast.success("Post added");
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/vlog/${id}`); load(); };
  return (
    <div className="space-y-6">
      <form onSubmit={create} className="card-luxe grid sm:grid-cols-2 gap-3">
        <input required placeholder="Title" value={f.title} onChange={(e)=>setF({...f, title:e.target.value})} className="input-luxe sm:col-span-2"/>
        <input placeholder="YouTube URL" value={f.youtube_url} onChange={(e)=>setF({...f, youtube_url:e.target.value})} className="input-luxe"/>
        <input placeholder="Thumbnail URL" value={f.thumbnail_url} onChange={(e)=>setF({...f, thumbnail_url:e.target.value})} className="input-luxe"/>
        <textarea required placeholder="Excerpt" value={f.excerpt} onChange={(e)=>setF({...f, excerpt:e.target.value})} className="input-luxe sm:col-span-2" rows={2}/>
        <button className="btn-gold sm:col-span-2">Publish</button>
      </form>
      <ul className="space-y-3">
        {rows.map(v => (
          <li key={v.id} className="card-luxe flex gap-4">
            <img src={v.thumbnail_url} alt="" className="w-32 h-20 rounded-lg object-cover"/>
            <div className="flex-1"><div className="font-serif text-lg">{v.title}</div><p className="text-xs text-white/80 mt-1">{v.excerpt}</p></div>
            <button onClick={() => del(v.id)} className="text-white/80 hover:text-[#F02078]"><Trash2 className="w-4 h-4"/></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SchedulePanel() {
  const [rows, setRows] = useState([]);
  const blank = { title: "", day: "Sunday", time: "", kind: "service", youtube_url: "", description: "" };
  const [f, setF] = useState(blank);
  const load = () => api.get("/admin/schedule").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    await api.post("/admin/schedule", f);
    setF(blank); load(); toast.success("Added");
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/schedule/${id}`); load(); };
  return (
    <div className="space-y-6">
      <form onSubmit={create} className="card-luxe grid sm:grid-cols-2 gap-3">
        <input required placeholder="Title" value={f.title} onChange={(e)=>setF({...f, title:e.target.value})} className="input-luxe"/>
        <input required placeholder="Time (e.g. 10:00 AM EST)" value={f.time} onChange={(e)=>setF({...f, time:e.target.value})} className="input-luxe"/>
        <select value={f.day} onChange={(e)=>setF({...f, day:e.target.value})} className="input-luxe">
          {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map(d=><option key={d}>{d}</option>)}
        </select>
        <select value={f.kind} onChange={(e)=>setF({...f, kind:e.target.value})} className="input-luxe">
          <option value="service">Service</option><option value="bible-study">Bible Study</option><option value="prayer">Prayer</option>
        </select>
        <input placeholder="YouTube URL" value={f.youtube_url} onChange={(e)=>setF({...f, youtube_url:e.target.value})} className="input-luxe sm:col-span-2"/>
        <textarea placeholder="Description" value={f.description} onChange={(e)=>setF({...f, description:e.target.value})} className="input-luxe sm:col-span-2" rows={2}/>
        <button className="btn-gold sm:col-span-2">Add to schedule</button>
      </form>
      <ul className="space-y-3">
        {rows.map(s => (
          <li key={s.id} className="card-luxe flex justify-between items-center">
            <div>
              <div className="font-serif text-lg">{s.title}</div>
              <div className="text-xs text-white/80">{s.day} · {s.time} · {s.kind}</div>
            </div>
            <button onClick={() => del(s.id)} className="text-white/80 hover:text-[#F02078]"><Trash2 className="w-4 h-4"/></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
